/** @jsx jsx */
import { jsx } from '@emotion/core';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import FavoriteIcon from '@material-ui/icons/Favorite';
import FavoriteBorderIcon from '@material-ui/icons/FavoriteBorder';

import { MainButton, Title } from 'components';
import { ChallengeResultType } from 'interfaces';
import {
  challengeResultUnvote,
  challengeResultVote,
} from 'actions/challenge.actions';
import { challengeResultVotingSelector } from 'reducers/challenge.reducer';
import { LOADING_STATUS } from 'constants/status';
import {
  cssDisplayBlock,
  cssMarginRight,
  cssVoteButton,
  StyledCircularProgress,
} from './styles';

const Voting = ({
  result,
  isMe = false,
  hasMyVote = false,
  withVotingButton = false,
  getChallengeResultsByUser,
}: {
  result: ChallengeResultType;
  isMe: boolean;
  hasMyVote: boolean;
  withVotingButton: boolean;
  getChallengeResultsByUser?: () => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { status } = useSelector(challengeResultVotingSelector(result.id));

  const handleVote = () => {
    dispatch(challengeResultVote.request(result.id, getChallengeResultsByUser));
  };

  const handleUnvote = () => {
    dispatch(
      challengeResultUnvote.request(result.id, getChallengeResultsByUser),
    );
  };

  const getFavoriteButtonContent = (
    Icon: typeof FavoriteIcon | typeof FavoriteBorderIcon,
    translation: string,
  ): JSX.Element => (
    <React.Fragment>
      {status === LOADING_STATUS.PENDING ? (
        <StyledCircularProgress size={22} />
      ) : (
        <Icon fontSize="large" css={cssMarginRight} />
      )}{' '}
      <strong>{translation}</strong>
    </React.Fragment>
  );

  return (
    <div css={{ textAlign: 'center' }}>
      <Title
        position="center"
        textCase="lower"
        css={cssDisplayBlock}
        color={isMe ? 'secondary' : 'info'}
        isParagraph
      >
        <FavoriteIcon fontSize="large" />{' '}
        {t('challenges.vote', { count: result.voters?.length ?? 0 })}
      </Title>
      {withVotingButton && !isMe && (
        <React.Fragment>
          {hasMyVote ? (
            <MainButton
              variant="contained"
              type="button"
              css={cssVoteButton}
              onClick={handleUnvote}
              disabled={status === LOADING_STATUS.PENDING}
            >
              {getFavoriteButtonContent(
                FavoriteIcon,
                t('photo_contest.rank.i_unvote'),
              )}
            </MainButton>
          ) : (
            <MainButton
              variant="outlined"
              type="button"
              css={cssVoteButton}
              onClick={handleVote}
              disabled={status === LOADING_STATUS.PENDING}
            >
              {getFavoriteButtonContent(
                FavoriteBorderIcon,
                t('photo_contest.rank.i_vote'),
              )}
            </MainButton>
          )}
        </React.Fragment>
      )}
    </div>
  );
};

export default Voting;
